import React, { useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import api from '../../services/Api';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiCheck,
  FiBriefcase,
  FiAlertCircle,
} from 'react-icons/fi';

const BusinessTypeSettings = () => {
  const [types, setTypes] = useState([]);
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTypes = async () => {
    try {
      const res = await api.get('/business-type');
      setTypes(res.data?.businessTypes || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load business types');
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Business type name is required');
      return;
    }

    setLoading(true);

    try {
      if (editingId) {
        await api.patch(`/business-type/${editingId}`, {
          name: trimmedName,
          isActive: true,
        });
        toast.success('Business type updated successfully!');
      } else {
        await api.post('/business-type', {
          name: trimmedName,
          isActive: true,
        });
        toast.success('Business type added successfully!');
      }

      setName('');
      setEditingId(null);
      fetchTypes();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (_id) => {
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-white rounded-lg pointer-events-auto flex flex-col overflow-hidden border border-gray-200`}
        >
          <div className="p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <FiAlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Delete Business Type
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  Are you sure you want to delete this business type? This
                  action cannot be undone.
                </p>
              </div>
            </div>
          </div>
          <div className="flex border-t border-gray-200">
            <button
              onClick={async () => {
                toast.dismiss(t.id);
                try {
                  await api.delete(`/business-type/${_id}`);
                  toast.success('Business type deleted successfully!');
                  fetchTypes();
                } catch (err) {
                  console.error(err);
                  toast.error('Failed to delete business type');
                }
              }}
              className="flex-1 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 focus:outline-none transition-colors"
            >
              Delete
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border-l border-gray-200 focus:outline-none transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        duration: Infinity,
        position: 'top-center',
      },
    );
  };

  const handleEdit = (type) => {
    setName(type.name);
    setEditingId(type._id);
  };

  const handleCancelEdit = () => {
    setName('');
    setEditingId(null);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Toaster Component */}
      <Toaster
        position="top-right"
        toastOptions={{
          success: {
            style: {
              background: '#10b981',
              color: 'white',
              padding: '12px',
              fontSize: '14px',
            },
            iconTheme: {
              primary: 'white',
              secondary: '#10b981',
            },
          },
          error: {
            style: {
              background: '#ef4444',
              color: 'white',
              padding: '12px',
              fontSize: '14px',
            },
            iconTheme: {
              primary: 'white',
              secondary: '#ef4444',
            },
          },
          loading: {
            style: {
              background: '#3b82f6',
              color: 'white',
              padding: '12px',
              fontSize: '14px',
            },
          },
        }}
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <FiBriefcase className="w-8 h-8 text-black" />
          <h1 className="text-2xl font-bold text-gray-900">Business Types</h1>
        </div>
        <p className="text-gray-600">
          Manage and organize your business categories
        </p>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {editingId ? 'Edit Business Type' : 'Add New Business Type'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="businessType"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Business Type Name
            </label>
            <div className="flex gap-3">
              <input
                id="businessType"
                type="text"
                placeholder="e.g., LLC, Non-profitable ..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-0 focus:outline-none transition-all outline-none"
                autoFocus
              />

              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2.5 bg-black text-white rounded-lg disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2 hover:bg-gray-800"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : editingId ? (
                  <>
                    <FiCheck className="w-4 h-4" />
                    Update
                  </>
                ) : (
                  <>
                    <FiPlus className="w-4 h-4" />
                    Add
                  </>
                )}
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center gap-2"
                >
                  <FiX className="w-4 h-4" />
                  Cancel
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* List Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            All Business Types
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({types.length} {types.length === 1 ? 'item' : 'items'})
            </span>
          </h2>
        </div>

        {Array.isArray(types) && types.length > 0 ? (
          <div className="grid gap-3">
            {types.map((type, index) => (
              <div
                key={type._id}
                className="group bg-white rounded-lg border border-gray-200 transition-all duration-200"
                style={{
                  animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both`,
                }}
              >
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                      <FiBriefcase className="w-5 h-5 text-black" />
                    </div>
                    <span className="text-gray-900 font-medium">
                      {type.name}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleEdit(type)}
                      className="p-2 text-black hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <FiEdit2 className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => handleDelete(type._id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <FiBriefcase className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No business types found</p>
            <p className="text-gray-400 text-sm mt-1">
              Add your first business type using the form above
            </p>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeInUp 0.3s ease-out;
        }

        @keyframes enter {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-enter {
          animation: enter 0.2s ease-out;
        }

        @keyframes leave {
          from {
            opacity: 1;
            transform: scale(1);
          }
          to {
            opacity: 0;
            transform: scale(0.9);
          }
        }

        .animate-leave {
          animation: leave 0.15s ease-in forwards;
        }
      `}</style>
    </div>
  );
};

export default BusinessTypeSettings;
